import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { FaCheckCircle, FaClock, FaTimesCircle } from "react-icons/fa";
import { getInvoiceStatus, getInvoiceStatusByPayment } from "../api/payments";
import { getSubscription } from "../api/subscription";
import { queueEvent, flushEvents } from "../analytics";
import { EVENTS } from "../analytics/events";
import { Button, Card, Spinner } from "../ui";
import Logo from "../components/site/Logo";
import SiteBackdrop from "../components/site/SiteBackdrop";

// Landing page MyFatoorah redirects back to. We don't trust the redirect —
// we reconcile the invoice with the backend (which re-verifies with MyFatoorah)
// and/or poll the subscription until premium flips. On cancel/failure MyFatoorah
// hits this URL with ?status=error, so we stop immediately instead of polling.
//
// The invoiceId normally comes from localStorage (written before the redirect);
// if that's gone (cleared storage, different browser context) we fall back to
// the ?paymentId= MyFatoorah appends. Either way the server also runs a
// 5-minute reconcile sweep, so a timeout here is not a lost purchase.
const MAX_ATTEMPTS = 30;
const INTERVAL_MS = 3000;

const CheckoutCallback = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [state, setState] = useState("checking"); // checking | success | timeout | canceled

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    let invoiceId = localStorage.getItem("mf_invoice");
    const paymentId = params.get("paymentId") || params.get("PaymentId");
    // Written by PlanCards before the gateway redirect — closes the funnel
    // with the same plan/source the purchase started with.
    const plan = localStorage.getItem("mf_plan") || null;
    const source = localStorage.getItem("mf_source") || "checkout";
    let attempts = 0;
    let timer;
    let cancelled = false;

    // The outcome is terminal for this funnel — clear the breadcrumbs and
    // push the batch out now instead of waiting for the timed flush.
    const trackOutcome = (event, extra = {}) => {
      queueEvent(event, { plan, method: "myfatoorah", source, ...extra });
      localStorage.removeItem("mf_plan");
      localStorage.removeItem("mf_source");
      flushEvents();
    };

    // MyFatoorah's ErrorUrl (cancel/failed) — don't poll, just tell the user.
    // Be lenient: the gateway may append its own params and mangle the value.
    if ((params.get("status") || "").toLowerCase().startsWith("error")) {
      localStorage.removeItem("mf_invoice");
      trackOutcome(EVENTS.PAYWALL_PURCHASE_CANCELLED, {
        reason: "gateway_error_url",
      });
      setState("canceled");
      return () => {};
    }

    const finishSuccess = () => {
      localStorage.removeItem("mf_invoice");
      trackOutcome(EVENTS.PAYWALL_PURCHASE_SUCCEEDED);
      queryClient.invalidateQueries({ queryKey: ["subscription"] });
      if (!cancelled) setState("success");
    };

    const poll = async () => {
      attempts += 1;
      try {
        let paid = false;
        let r = null;
        if (invoiceId) {
          r = await getInvoiceStatus(invoiceId);
        } else if (paymentId) {
          r = await getInvoiceStatusByPayment(paymentId);
          if (r?.invoiceId) {
            invoiceId = r.invoiceId;
            localStorage.setItem("mf_invoice", invoiceId);
          }
        }
        if (r) {
          paid = r.paid;
          // Terminal non-paid status → stop polling, show "not completed".
          if (
            !paid &&
            (r.status === "failed" ||
              r.status === "expired" ||
              r.status === "canceled")
          ) {
            localStorage.removeItem("mf_invoice");
            trackOutcome(EVENTS.PAYWALL_PURCHASE_FAILED, { reason: r.status });
            if (!cancelled) setState("canceled");
            return;
          }
        }
        if (!paid) {
          const sub = await getSubscription();
          paid = !!sub.isPremium;
        }
        if (paid) return finishSuccess();
      } catch (e) {
        // transient — keep polling
      }
      if (attempts >= MAX_ATTEMPTS) {
        // Unconfirmed, not necessarily lost — the webhook may still land.
        trackOutcome(EVENTS.PAYWALL_PURCHASE_FAILED, {
          reason: "confirmation_timeout",
        });
        if (!cancelled) setState("timeout");
        return;
      }
      timer = setTimeout(poll, INTERVAL_MS);
    };

    poll();
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [queryClient]);

  const iconTile = (tone, icon) => (
    <span
      className={`mx-auto mb-5 w-16 h-16 rounded-2xl border flex items-center justify-center ${tone}`}
    >
      {icon}
    </span>
  );

  return (
    <div
      dir="rtl"
      className="relative isolate min-h-screen overflow-hidden bg-header font-sans text-white flex flex-col items-center justify-center px-4 py-10"
    >
      <SiteBackdrop />
      <Logo className="w-14 h-14 mb-6" />
      <Card
        tone="solid"
        pad="lg"
        className="max-w-md w-full text-center shadow-2xl shadow-black/30"
      >
        <div role="status" aria-live="polite">
          {state === "checking" && (
            <>
              {iconTile(
                "bg-white/10 border-white/15 text-primary",
                <Spinner size={28} />
              )}
              <h1 className="text-xl font-extrabold mb-2">
                جارٍ تأكيد الدفع...
              </h1>
              <p className="text-white/70">يرجى الانتظار لحظات</p>
            </>
          )}
          {state === "success" && (
            <>
              {iconTile(
                "bg-success/15 border-success/40 text-success",
                <FaCheckCircle size={30} aria-hidden="true" />
              )}
              <h1 className="text-2xl font-extrabold mb-2">
                تم تفعيل الاشتراك المميز!
              </h1>
              <p className="text-white/70 mb-6">استمتع بكل المزايا</p>
              <Button block size="lg" onClick={() => navigate("/app")}>
                المتابعة
              </Button>
            </>
          )}
          {state === "canceled" && (
            <>
              {iconTile(
                "bg-danger/15 border-danger/40 text-danger",
                <FaTimesCircle size={30} aria-hidden="true" />
              )}
              <h1 className="text-xl font-extrabold mb-2">لم يكتمل الدفع</h1>
              <p className="text-white/70 leading-relaxed mb-6">
                تم إلغاء عملية الدفع أو لم تكتمل. لم يتم خصم أي مبلغ.
              </p>
              <div className="flex gap-3">
                <Button className="flex-1" onClick={() => navigate("/pricing")}>
                  إعادة المحاولة
                </Button>
                <Button
                  variant="secondary"
                  className="flex-1"
                  onClick={() => navigate("/app")}
                >
                  المتابعة
                </Button>
              </div>
            </>
          )}
          {state === "timeout" && (
            <>
              {iconTile(
                "bg-primary/15 border-primary/30 text-primary",
                <FaClock size={28} aria-hidden="true" />
              )}
              <h1 className="text-xl font-extrabold mb-2">
                لم نتمكّن من تأكيد الدفع
              </h1>
              <p className="text-white/70 leading-relaxed mb-6">
                إن كنت قد أتممت الدفع فسيُفعَّل اشتراكك خلال دقائق. وإن ألغيت
                العملية فلم يُخصم أي مبلغ.
              </p>
              <div className="flex gap-3">
                <Button className="flex-1" onClick={() => navigate("/pricing")}>
                  إعادة المحاولة
                </Button>
                <Button
                  variant="secondary"
                  className="flex-1"
                  onClick={() => navigate("/app")}
                >
                  المتابعة
                </Button>
              </div>
            </>
          )}
        </div>
      </Card>
    </div>
  );
};

export default CheckoutCallback;
